import {
  DEFAULT_PREFERENCES,
  GalleryDisplayPreferences,
  GalleryPaginationPreferences,
  LocationsFilters,
  UserPreferences
} from '../types/Preferences';
import {SearchCriteria} from '../../gallery/types/SearchCriteria';

const STORAGE_KEY = 'userPreferences';

class PreferencesService {
  getPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return DEFAULT_PREFERENCES;
      }

      const parsed = JSON.parse(stored);
      return this.mergeWithDefaults(parsed);
    } catch (error) {
      console.error('Error loading preferences from localStorage:', error);
      return DEFAULT_PREFERENCES;
    }
  }

  savePreferences(preferences: UserPreferences): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving preferences to localStorage:', error);
    }
  }

  updateGalleryDisplay(display: Partial<GalleryDisplayPreferences>): void {
    const preferences = this.getPreferences();
    preferences.gallery.display = {
      ...preferences.gallery.display,
      ...display
    };
    this.savePreferences(preferences);
  }

  updateGalleryPagination(pagination: Partial<GalleryPaginationPreferences>): void {
    const preferences = this.getPreferences();
    preferences.gallery.pagination = {
      ...preferences.gallery.pagination,
      ...pagination
    };
    this.savePreferences(preferences);
  }

  updateGalleryFilters(filters: SearchCriteria): void {
    const preferences = this.getPreferences();
    preferences.gallery.filters = filters;
    this.savePreferences(preferences);
  }

  getGalleryDisplay(): GalleryDisplayPreferences {
    return this.getPreferences().gallery.display;
  }

  getGalleryPagination(): GalleryPaginationPreferences {
    return this.getPreferences().gallery.pagination;
  }

  getGalleryFilters(): SearchCriteria {
    return this.getPreferences().gallery.filters;
  }

  updateLocationsFilters(filters: LocationsFilters): void {
    const preferences = this.getPreferences();
    preferences.locations.filters = filters;
    this.savePreferences(preferences);
  }

  getLocationsFilters(): LocationsFilters {
    return this.getPreferences().locations.filters;
  }

  clearPreferences(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing preferences from localStorage:', error);
    }
  }

  private mergeWithDefaults(partial: any): UserPreferences {
    return {
      gallery: {
        display: {
          ...DEFAULT_PREFERENCES.gallery.display,
          ...(partial?.gallery?.display || {})
        },
        pagination: {
          ...DEFAULT_PREFERENCES.gallery.pagination,
          ...(partial?.gallery?.pagination || {})
        },
        filters: partial?.gallery?.filters || {}
      },
      locations: {
        filters: {
          ...DEFAULT_PREFERENCES.locations.filters,
          ...(partial?.locations?.filters || {})
        }
      }
    };
  }
}

export default new PreferencesService();

